export type AssistRoute = 'backlog' | 'websearch'

/**
 * Where an answer's content actually came from, which `route` cannot say on its
 * own: a question answered out of earlier meetings is fetched over the Backlog
 * route and so arrives as `route: 'backlog'`, indistinguishable from a real
 * ticket lookup. Optional throughout because cards saved before this field
 * existed carry none, and those keep being labelled by `route`.
 */
export type AssistSourceType = 'backlog' | 'websearch' | 'meeting'

/** Tone of a tag pill: Backlog results read warm, everything else neutral. */
export type AssistTagTone = 'neutral' | 'backlog'

export interface AssistSuggestion {
  id: string
  text: string
  route: AssistRoute
  /**
   * The transcript turn the question came from, copied verbatim by the AI
   * service — never a paraphrase of it.
   */
  quote?: string
  /** Who said `quote`; `null` when the segment could not be attributed. */
  speaker?: string | null
  /** When `quote` was said, in ms from the meeting start; `null` when unknown. */
  quoteTimestampMs?: number | null
  /**
   * Free-form context label the AI service picks (e.g. "Rủi ro", "Chi phí"). It
   * says why the question is worth reading and is unrelated to `route`, which is
   * what a click does. Shown verbatim, so it is not translatable; a missing label
   * just hides the pill.
   */
  tag?: string | null
  /** Which body of content answers this question. See {@link AssistSourceType}. */
  sourceType?: AssistSourceType
}

/** i18n key of a tag pill label, resolved against the caller's locale. */
export type AssistTagLabelKey =
  | 'copilotSourceBacklog'
  | 'copilotSourceMeeting'
  | 'copilotSourceWeb'
  | 'copilotTagTechnical'

/**
 * A tag pill's content. Labels the app owns are locale keys; labels the AI
 * service sends are literals shown as-is.
 */
export type AssistTagPillSpec =
  | { kind: 'locale'; labelKey: AssistTagLabelKey; tone: AssistTagTone }
  | { kind: 'literal'; label: string; tone: AssistTagTone }

/**
 * Resolves the tag pill for a card that only knows where its content came from.
 * A `null` route is not reported yet, so it is labelled Technical rather than
 * Web — nothing confirms a web result.
 *
 * `sourceType` is read before `route` because a meeting-sourced answer travels
 * the Backlog route: going by `route` alone would label earlier meetings as
 * Backlog. A card without one — saved before the field existed — falls through
 * to the `route` labels unchanged.
 *
 * @param route The route the answer was served over; `null` while pending.
 * @param sourceType What the answer was drawn from, when the service reports it.
 */
export function resolveAssistSourceTag(
  route: AssistRoute | null,
  sourceType?: AssistSourceType | null
): AssistTagPillSpec {
  if (sourceType === 'meeting') {
    return { kind: 'locale', labelKey: 'copilotSourceMeeting', tone: 'neutral' }
  }
  if (route === 'backlog') {
    return { kind: 'locale', labelKey: 'copilotSourceBacklog', tone: 'backlog' }
  }
  if (route === 'websearch') {
    return { kind: 'locale', labelKey: 'copilotSourceWeb', tone: 'neutral' }
  }
  return { kind: 'locale', labelKey: 'copilotTagTechnical', tone: 'neutral' }
}

/**
 * Resolves the tag pill for a suggested question. A meeting source outranks
 * everything — it is the one distinction `route` cannot carry — then a Backlog
 * route outranks the AI's own label so the source distinction stays visible;
 * otherwise the AI's label wins and only a missing one falls back to the Web pill.
 */
export function resolveSuggestionTag(suggestion: AssistSuggestion): AssistTagPillSpec {
  if (suggestion.sourceType === 'meeting') {
    return { kind: 'locale', labelKey: 'copilotSourceMeeting', tone: 'neutral' }
  }
  if (suggestion.route === 'backlog') {
    return { kind: 'locale', labelKey: 'copilotSourceBacklog', tone: 'backlog' }
  }
  const tag = suggestion.tag?.trim()
  return tag
    ? { kind: 'literal', label: tag, tone: 'neutral' }
    : { kind: 'locale', labelKey: 'copilotSourceWeb', tone: 'neutral' }
}

/**
 * Turn one route on or off.
 *
 * The last one stays on. That rule began as a rule about this screen — the
 * suggestion list must not be left with every category hidden — and the list
 * is no longer filtered here at all. It outlives its reason: these routes are
 * what the Copilot may answer from, and a selection of nothing asks the server
 * to answer questions with nothing to answer them out of.
 *
 * @param selected Routes currently on.
 * @param category The chip the reader pressed.
 * @returns The same set when the press would clear every route; otherwise a new set.
 */
export function toggleSearchCategory(
  selected: ReadonlySet<AssistRoute>,
  category: AssistRoute
): ReadonlySet<AssistRoute> {
  if (!selected.has(category)) {
    const next = new Set(selected)
    next.add(category)
    return next
  }
  if (selected.size === 1) return selected
  const next = new Set(selected)
  next.delete(category)
  return next
}

/**
 * A run of suggestions that share one verbatim transcript quote. Each group owns
 * its own speaker line, so two questions raised by the same sentence are shown
 * under one header rather than repeating it.
 */
export interface AssistQuoteGroup {
  /** Stable React key: the quote's identity, or the suggestion id when there is none. */
  key: string
  quote: string | null
  speaker: string | null
  quoteTimestampMs: number | null
  suggestions: AssistSuggestion[]
}

/**
 * Groups suggestions by the quote they came from. A suggestion without a quote
 * becomes a group of its own so it still renders, just without a header.
 */
export function groupSuggestionsByQuote(
  suggestions: AssistSuggestion[]
): AssistQuoteGroup[] {
  const groups: AssistQuoteGroup[] = []
  const groupsByQuote = new Map<string, AssistQuoteGroup>()

  for (const suggestion of suggestions) {
    const quote = suggestion.quote?.trim() ? suggestion.quote : null
    if (!quote) {
      groups.push({
        key: `suggestion:${suggestion.id}`,
        quote: null,
        speaker: null,
        quoteTimestampMs: null,
        suggestions: [suggestion],
      })
      continue
    }

    const speaker = suggestion.speaker ?? null
    const quoteTimestampMs = suggestion.quoteTimestampMs ?? null
    const key = `quote:${[quote, speaker ?? '', quoteTimestampMs ?? ''].join('\u0000')}`
    const existing = groupsByQuote.get(key)
    if (existing) {
      existing.suggestions.push(suggestion)
      continue
    }

    const group: AssistQuoteGroup = {
      key,
      quote,
      speaker,
      quoteTimestampMs,
      suggestions: [suggestion],
    }
    groupsByQuote.set(key, group)
    groups.push(group)
  }

  return groups
}

export interface AssistSource {
  title: string
  url: string | null
  issueKey: string | null
}

interface AssistCardCore {
  cardId: string
  elapsedMs?: number
}

export interface QuerySuggestionCard extends AssistCardCore {
  type: 'query_suggestion'
  suggestions: AssistSuggestion[]
}

export interface AnswerCard extends AssistCardCore {
  type: 'answer'
  status: 'pending' | 'ready' | 'failed'
  question: string
  route: AssistRoute | null
  /**
   * What the answer was drawn from. `null` alongside a `null` route, i.e. while
   * the card is pending; a `ready`/`failed` card may report a different one than
   * its own pending frame did, and `upsertAssistCard` keeps the last by cardId.
   */
  sourceType?: AssistSourceType | null
  answer: string
  sources: AssistSource[]
  error: AssistErrorBody | null
}

export interface BacklogTicketCard extends AssistCardCore {
  type: 'backlog_ticket'
  issueKey: string
  summary: string
  assignee: string
  dueDate: string
  ticketStatus: string
  link: string
}

export interface ConflictWarningCard extends AssistCardCore {
  type: 'conflict_warning'
  issueKey: string
  field: 'deadline' | 'status'
  inMeeting: string
  inBacklog: string
  link: string
}

export type AssistCard =
  | QuerySuggestionCard
  | AnswerCard
  | BacklogTicketCard
  | ConflictWarningCard

export type AssistAskInput = { suggestionId: string } | { text: string }

/**
 * Why the AI service turned a question down.
 *
 * `reason` separates refusals that `code` cannot: five different Backlog
 * refusals all arrive as `not_supported`, and only one of them is something
 * the reader can go and fix. Optional because a service that predates the
 * field sends none — see `resolveBacklogRefusal`.
 */
export interface AssistErrorBody {
  code: string
  message: string
  reason?: string
}

export type AssistAskError = AssistErrorBody

/**
 * An answer card that has not finished being answered.
 *
 * Two callers, one rule. The room event stream drops these because a `ready` or
 * `failed` frame replaces them moments later, so storing one writes a row to
 * replay something already stale. The End Meeting save drops them for the
 * opposite reason: the socket is closing, so that replacement is never coming
 * and a stored card would spin for good.
 *
 * Deliberately takes the loose shape rather than `AssistCard`: one caller holds
 * a raw frame off the socket, and the three other kinds of card carry no
 * `status` and are unaffected either way.
 */
export function isPendingAnswerCard(card: { type?: unknown; status?: unknown }): boolean {
  return card.type === 'answer' && card.status === 'pending'
}

/**
 * Whether a Copilot card is safe to share with every participant in a room.
 * Answers and action results are derived from one participant's question and
 * permissions, so only AI-generated question suggestions cross this boundary.
 */
export function isSharedAssistCard(card: unknown): card is QuerySuggestionCard {
  return (
    typeof card === 'object' &&
    card !== null &&
    (card as { type?: unknown }).type === 'query_suggestion'
  )
}

export function upsertAssistCard(current: AssistCard[], card: AssistCard): AssistCard[] {
  const at = current.findIndex((c) => c.cardId === card.cardId)
  if (at === -1) return [...current, card]
  const next = [...current]
  next[at] = card
  return next
}

/**
 * The array object from last time, when nothing in it has changed.
 *
 * For a consumer that watches array identity rather than contents —
 * `useAutoScroll` is the one in this panel. A list derived with `filter` is a
 * new array on every render, so such a consumer reads "new content arrived"
 * from a change that happened somewhere else entirely.
 *
 * Compared by element identity, which is exactly what an upsert changes: a card
 * the socket revised is a different object, a card nobody touched is the same
 * one.
 *
 * @param next     The freshly derived list.
 * @param previous Whatever this returned the time before.
 * @returns `previous` when the two hold the same cards in the same order.
 * @example
 *   ref.current = stableCardList(items.filter(isMine), ref.current)
 */
export function stableCardList(next: AssistCard[], previous: AssistCard[]): AssistCard[] {
  if (next.length !== previous.length) return next
  return next.every((card, index) => card === previous[index]) ? previous : next
}

/**
 * The cards the panel draws. Every question the AI raised is an item, answered or
 * not, and each one states the turn it came from — a reader has to know which
 * conversation raised a question before opening anything.
 *
 * Cards are cast straight off the socket rather than validated, so the fields the
 * panel cannot draw without are checked here: an answer with no question has
 * nothing to show, and a suggestion card with no suggestion list would throw
 * further down.
 */
export function selectCopilotItems(cards: AssistCard[]): AssistCard[] {
  return cards.filter((card) => {
    if (card.type === 'answer') return Boolean(card.question?.trim())
    if (card.type === 'query_suggestion') return Array.isArray(card.suggestions)
    return true
  })
}

/**
 * How many items deserve the unread badge: every question the AI raised, the
 * answers it returned for questions nobody suggested, and the ticket and
 * conflict cards.
 *
 * A question still waiting for its answer counts (BRAI-657 follow-up). It used
 * not to, on the reasoning that it arrives with the transcript and is not news
 * on its own — but that holds only while the panel is open. Closed, an
 * unanswered question is the entire thing the badge exists to announce, and
 * counting only answered ones left it reading zero for a whole meeting:
 * answers arrive when the reader asks, and asking means the panel is open,
 * which clears the count.
 */
export function countCopilotItems(cards: AssistCard[]): number {
  const items = selectCopilotItems(cards)
  const questions = new Set<string>()
  for (const card of items) {
    if (card.type !== 'query_suggestion') continue
    for (const suggestion of card.suggestions) questions.add(suggestion.text)
  }

  let total = 0
  for (const card of items) {
    if (card.type === 'query_suggestion') {
      total += card.suggestions.length
      continue
    }
    // A matching answer renders inside its suggestion rather than on its own,
    // so counting it here would count that row twice.
    if (card.type === 'answer' && questions.has(card.question)) continue
    total += 1
  }
  return total
}

const TYPE_PRIORITY: Record<AssistCard['type'], number> = {
  answer: 0,
  conflict_warning: 1,
  backlog_ticket: 2,
  query_suggestion: 3,
}

export interface AssistDisplayRow {
  card: AssistCard
  index: number
}

function bySectionOrder(a: AssistDisplayRow, b: AssistDisplayRow): number {
  return TYPE_PRIORITY[a.card.type] - TYPE_PRIORITY[b.card.type] || a.index - b.index
}

export function toAssistDisplayRows(cards: AssistCard[]): AssistDisplayRow[] {
  return cards.map((card, index) => ({ card, index })).sort(bySectionOrder)
}

/**
 * An answer that belongs to a rendered suggestion. The link is the question
 * wording: an answer card does not record which suggestion produced it.
 */
export interface NestedAssistAnswer {
  card: AnswerCard
  rowKey: string
}

/**
 * Pending answers stay open so the loading state is visible; the rest follow the
 * user's expand/collapse toggle.
 */
export function isAnswerExpanded(
  answer: NestedAssistAnswer | undefined,
  expandedCardIds: string[]
): boolean {
  if (!answer) return false
  return answer.card.status === 'pending' || expandedCardIds.includes(answer.rowKey)
}
